import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { availableFromCount, onHandQty } from '../lib/stock'
import { useStockInfo } from '../hooks/use-stock-info'
import type { StockArticleInfo } from '../hooks/use-stock-info'
import { inventoryApi } from '@/api/endpoints/inventory'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useCountSessionGuard } from '@/features/inventory-count/hooks/use-count-session-guard'
import {
  formatStockDisplay,
  parseStockAmount,
} from '@/shared/lib/stock-quantity'

export type StockActionKind = 'receive' | 'setOnHand' | 'setBuffer'

const COPY: Record<
  StockActionKind,
  { title: string; label: string; ok: string; fail: string }
> = {
  receive: {
    title: 'dialog.receiveTitle',
    label: 'dialog.quantityReceive',
    ok: 'toast.inventoryReceived',
    fail: 'toast.inventoryReceiveFailed',
  },
  setOnHand: {
    title: 'dialog.setOnHandTitle',
    label: 'dialog.quantityOnHand',
    ok: 'toast.onHandUpdated',
    fail: 'toast.onHandUpdateFailed',
  },
  setBuffer: {
    title: 'dialog.setBufferTitle',
    label: 'dialog.quantityBuffer',
    ok: 'toast.bufferUpdated',
    fail: 'toast.bufferUpdateFailed',
  },
}

interface StockActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: StockActionKind
  sku: string
  name: string
  restaurantId: number
  /** Called after a successful write (e.g. invalidate the caller's list query). */
  onSuccess?: () => void
  /**
   * The article's counting mode and unit, when the caller already has them; otherwise the dialog
   * reads them itself (CHG-2026-09-stock-usage-and-price-twins, D9). Until they are known the
   * input takes whole numbers only, exactly as before.
   */
  stock?: StockArticleInfo
}

/**
 * The typed quantity as a number, or null. A whole-number article keeps the rule it always had
 * (digits only); an article with "Teilmengen zählen" also takes up to 3 decimals with a comma or a
 * dot. The value sent is the typed one, never the result of float arithmetic.
 */
export function parseStockActionInput(
  value: string,
  fractional: boolean,
): number | null {
  if (!fractional)
    return /^\d+$/.test(value.trim()) ? Number(value.trim()) : null
  const parsed = parseStockAmount(value)
  return parsed.ok ? parsed.value : null
}

/**
 * The one stock dialog for single-SKU corrections, shared by the inventory page and the
 * menu planner. Levels are fetched fresh on open so a count never runs against numbers
 * loaded minutes ago; the first write creates a missing inventory row (server upserts).
 */
export function StockActionDialog({
  open,
  onOpenChange,
  action,
  sku,
  name,
  restaurantId,
  onSuccess,
  stock,
}: StockActionDialogProps) {
  const { t: tInventory } = useTranslation('inventory')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const [value, setValue] = useState('')
  const fetchedStock = useStockInfo(open && !stock ? [sku] : [])
  const info = stock ?? fetchedStock.get(sku)
  const fractional = info?.fractional === true
  // A dish, variant or not-tracked article: the server refuses the write (product_has_no_stock),
  // so the dialog says why instead of offering a save that cannot succeed.
  const noStock = info?.noStock ?? null

  // One key per submission (D6): a retried Wareneingang after a timeout reuses it and books once;
  // a freshly opened dialog starts without one.
  const clientRefRef = useRef<string | null>(null)

  useEffect(() => {
    if (open) {
      setValue('')
      clientRefRef.current = null
    }
  }, [open, action])

  const { data: levels, isPending: levelsLoading } = useQuery({
    queryKey: ['inventory', sku, restaurantId],
    queryFn: () => inventoryApi.getOne(sku, restaurantId),
    enabled: open,
  })

  // Null quantities mean "no inventory row yet", not an error.
  const reserved = levels?.reservedQty ?? 0
  const buffer = levels?.safetyBufferQty ?? 0

  const parsed = parseStockActionInput(value, fractional)
  const clamped = action === 'setOnHand' && parsed != null && parsed < reserved

  const mutation = useMutation({
    mutationFn: (n: number) => {
      switch (action) {
        case 'receive':
          if (!clientRefRef.current) clientRefRef.current = newIdempotencyKey()
          return inventoryApi.receive(
            sku,
            restaurantId,
            n,
            clientRefRef.current,
          )
        case 'setOnHand':
          return inventoryApi.setAvailable(
            sku,
            restaurantId,
            availableFromCount(n, reserved),
          )
        case 'setBuffer':
          return inventoryApi.setSafetyBuffer(sku, restaurantId, n)
      }
    },
    onSuccess: () => {
      clientRefRef.current = null
      toast.success(tInventory(COPY[action].ok))
      // Mark the sku-scoped copies stale (product tab, this dialog's next open)
      // without refetching for a dialog that is about to unmount.
      queryClient.invalidateQueries({
        queryKey: ['inventory', sku],
        refetchType: 'none',
      })
      onSuccess?.()
      onOpenChange(false)
    },
    onError: () => toast.error(tInventory(COPY[action].fail)),
  })

  const countGuard = useCountSessionGuard(restaurantId)

  const submit = () => {
    if (parsed == null || (action === 'receive' && parsed === 0)) {
      toast.error(tInventory('toast.invalidNumber'))
      return
    }
    // The safety buffer moves no stock, so an open count is no reason to ask.
    if (action === 'setBuffer') mutation.mutate(parsed)
    else void countGuard.guard(() => mutation.mutate(parsed))
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tInventory(COPY[action].title)}</DialogTitle>
            <DialogDescription>{`${name} (${sku})`}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2 rounded-md bg-muted/50 p-3 text-sm">
            {(
              [
                ['table.onHand', onHandQty(levels?.availableQty, reserved)],
                ['table.reserved', reserved],
                ['table.safetyBuffer', buffer],
                ['table.sellable', levels?.sellableQty ?? 0],
              ] as const
            ).map(([key, qty]) => (
              <div key={key} className="min-w-0">
                <p className="truncate text-xs text-muted-foreground">
                  {tInventory(key)}
                </p>
                <p className="font-mono font-medium">
                  {levelsLoading ? '…' : formatStockDisplay(qty)}
                </p>
              </div>
            ))}
          </div>
          <div className="space-y-2 py-2">
            <Label htmlFor="stock-action-qty">
              {tInventory(COPY[action].label)}
              {info?.fractional ? (
                <span className="font-normal text-muted-foreground">
                  {tInventory('dialog.inUnit', { unit: info.unit })}
                </span>
              ) : null}
            </Label>
            {fractional ? (
              // "Teilmengen zählen": a text field, because a number field drops the de-AT comma.
              <Input
                id="stock-action-qty"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            ) : (
              <Input
                id="stock-action-qty"
                type="number"
                inputMode="numeric"
                step={1}
                min={0}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
            {info?.fractional && (
              <p className="text-sm text-muted-foreground">
                {tInventory('dialog.decimalHint', { unit: info.unit })}
              </p>
            )}
            {action === 'setOnHand' && !clamped && (
              <p className="text-sm text-muted-foreground">
                {tInventory('dialog.onHandHint', {
                  reserved: formatStockDisplay(reserved),
                })}
              </p>
            )}
            {noStock && (
              <p role="alert" className="text-sm text-destructive">
                {tInventory('noStock.actionsHint')}
              </p>
            )}
            {clamped && (
              <p className="text-sm text-destructive">
                {tInventory('dialog.clampHint', {
                  reserved: formatStockDisplay(reserved),
                })}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={submit}
              disabled={
                mutation.isPending ||
                countGuard.checking ||
                levelsLoading ||
                noStock != null
              }
            >
              {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
              {tCommon('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {countGuard.dialog}
    </>
  )
}
